import React, { useEffect, useState } from 'react';
import { Check, Package, ShoppingCart, Share2, Search, Rocket, Star, Users, ArrowRight, Sparkles } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ServicesPage = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const mainServices = [
    {
      icon: <Package className="w-8 h-8" />,
      title: "Custom Website Design for Authors",
      description: "A beautiful, functional website is the cornerstone of an author's success. We design fully customized websites that reflect your unique style and branding.",
      packages: [
        {
          name: "Essential",
          subtitle: "For emerging authors",
          price: "$799",
          features: [
            "Up to 3 pages with basic functionality",
            "Custom homepage design",
            "Book showcase page",
            "Contact page",
            "Responsive design",
            "Basic content management system"
          ]
        },
        {
          name: "Professional",
          subtitle: "Most popular",
          price: "$1,499",
          popular: true,
          features: [
            "Up to 5 pages",
            "Enhanced branding",
            "Blog integration",
            "Custom colors and fonts",
            "Newsletter integration",
            "Premium support"
          ]
        },
        {
          name: "Premium",
          subtitle: "Complete solution",
          price: "$2,999+",
          features: [
            "Unlimited pages",
            "Advanced features",
            "Full customization",
            "E-commerce integration",
            "Priority support",
            "1 year maintenance"
          ]
        }
      ]
    }
  ];

  const additionalServices = [
    {
      icon: <ShoppingCart className="w-6 h-6" />,
      title: "eCommerce Setup",
      price: "From $599",
      description: "Sell books, merchandise, and special editions directly from your site",
      features: [
        "WooCommerce or Shopify integration",
        "Secure payment processing",
        "Product pages setup",
        "Inventory management",
        "Shipping configuration"
      ]
    },
    {
      icon: <Share2 className="w-6 h-6" />,
      title: "Blog & Social Integration",
      price: "From $399",
      description: "Keep your audience engaged with integrated communication tools",
      features: [
        "Blog setup with categories",
        "Newsletter integration",
        "Social media feeds",
        "Contact form setup",
        "Email notifications"
      ]
    },
    {
      icon: <Search className="w-6 h-6" />,
      title: "SEO Optimization",
      price: "From $499",
      description: "Help readers find your books with author-focused SEO",
      features: [
        "Keyword research",
        "Meta tags optimization",
        "Technical SEO setup",
        "Analytics integration",
        "Performance monitoring"
      ]
    },
    {
      icon: <Rocket className="w-6 h-6" />,
      title: "Book Launch Pages",
      price: "From $299",
      description: "High-converting landing pages for your book launches",
      features: [
        "Custom design",
        "Countdown timer",
        "Pre-order integration",
        "Lead generation",
        "Analytics tracking"
      ]
    }
  ];

  const scrollToContact = () => {
    window.location.href = '/#contact';
  };

  return (
    <div className="min-h-screen bg-cream-50">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%231a1a2e' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
        <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-gold-200/20 rounded-full blur-3xl" />

        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className={`text-center max-w-4xl mx-auto transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-gold-500" />
              <span className="text-gold-600 text-sm font-sans tracking-[0.2em] uppercase">
                Our Services
              </span>
              <span className="w-8 h-px bg-gold-500" />
            </div>

            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-ink-900 mb-6">
              Services &
              <span className="block mt-2 text-gradient-gold">Pricing</span>
            </h1>

            <p className="font-body text-xl text-ink-600 leading-relaxed max-w-2xl mx-auto">
              Welcome to WriteToSite, where we specialize in building professional,
              high-performing websites tailored specifically for authors.
            </p>
          </div>
        </div>
      </section>

      {/* Feature Highlights */}
      <section className="py-16 bg-cream-100">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: <Package className="w-7 h-7" />, title: "Custom Website Design", desc: "Tailored solutions that showcase your books and author brand effectively." },
              { icon: <Star className="w-7 h-7" />, title: "Author-Focused Features", desc: "Book previews, reader engagement tools, and newsletter integration." },
              { icon: <Users className="w-7 h-7" />, title: "Grow Your Readership", desc: "Built-in marketing tools to help you connect with more readers." }
            ].map((item, i) => (
              <div
                key={i}
                className={`group p-8 bg-cream-50 border border-cream-200 transition-all duration-700 hover:border-gold-400/50 hover:shadow-luxury ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="w-14 h-14 flex items-center justify-center bg-gold-100 text-gold-600 rounded-lg mb-6 group-hover:scale-110 transition-transform duration-300">
                  {item.icon}
                </div>
                <h3 className="font-display text-xl font-semibold text-ink-900 mb-3">{item.title}</h3>
                <p className="font-body text-ink-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Services - Pricing Cards */}
      <section id="pricing" className="py-24 lg:py-32">
        <div className="container mx-auto px-6 lg:px-12">
          {mainServices.map((service, index) => (
            <div key={index}>
              <div className="text-center mb-16">
                <div className="inline-flex items-center gap-3 mb-6">
                  <span className="w-8 h-px bg-gold-500" />
                  <span className="text-gold-600 text-sm font-sans tracking-[0.2em] uppercase">
                    Pricing
                  </span>
                  <span className="w-8 h-px bg-gold-500" />
                </div>
                <h2 className="font-display text-3xl md:text-4xl font-bold text-ink-900 mb-4">{service.title}</h2>
                <p className="font-body text-ink-600 max-w-2xl mx-auto">{service.description}</p>
              </div>

              <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                {service.packages.map((pkg, idx) => (
                  <div
                    key={idx}
                    className={`relative transition-all duration-700 ${pkg.popular ? 'lg:-mt-4 lg:mb-4' : ''}`}
                  >
                    {pkg.popular && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20">
                        <div className="flex items-center gap-2 px-4 py-2 bg-ink-900 text-gold-400 text-sm font-sans tracking-wide rounded-full shadow-luxury">
                          <Sparkles className="w-4 h-4" />
                          <span>Most Popular</span>
                        </div>
                      </div>
                    )}

                    <div className={`relative h-full flex flex-col p-8 lg:p-10 transition-all duration-500 ${
                      pkg.popular
                        ? 'bg-ink-900 text-cream-50 shadow-luxury-lg border border-gold-500/30'
                        : 'bg-cream-50 text-ink-900 border border-cream-300 hover:border-gold-400/50 hover:shadow-luxury'
                    }`}>
                      {pkg.popular && (
                        <div className="absolute top-0 right-0 w-24 h-24 overflow-hidden">
                          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-gold-500/20 to-transparent transform rotate-45 translate-x-16 -translate-y-16" />
                        </div>
                      )}

                      <div className="mb-8">
                        <div className={`text-sm font-sans tracking-wide mb-2 ${pkg.popular ? 'text-gold-400' : 'text-gold-600'}`}>
                          {pkg.subtitle}
                        </div>
                        <h3 className="font-display text-3xl font-bold mb-4">{pkg.name}</h3>
                        <div className="flex items-baseline gap-1">
                          <span className={`text-2xl ${pkg.popular ? 'text-cream-300' : 'text-ink-500'}`}>$</span>
                          <span className="font-display text-5xl font-bold">{pkg.price.replace('$', '')}</span>
                        </div>
                      </div>

                      <div className={`h-px mb-8 ${pkg.popular ? 'bg-ink-700' : 'bg-cream-300'}`} />

                      <div className="flex-1 space-y-4 mb-8">
                        {pkg.features.map((feature, i) => (
                          <div key={i} className="flex items-start gap-3">
                            <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${
                              pkg.popular ? 'bg-gold-500/20 text-gold-400' : 'bg-gold-100 text-gold-600'
                            }`}>
                              <Check className="w-3 h-3" />
                            </div>
                            <span className={`font-body ${pkg.popular ? 'text-cream-200' : 'text-ink-700'}`}>
                              {feature}
                            </span>
                          </div>
                        ))}
                      </div>

                      <button
                        onClick={scrollToContact}
                        className={`w-full flex items-center justify-center gap-2 py-4 font-sans font-medium tracking-wide transition-all duration-300 group ${
                          pkg.popular
                            ? 'bg-gradient-to-r from-gold-500 to-gold-600 text-ink-900 hover:from-gold-400 hover:to-gold-500 shadow-gold'
                            : 'bg-ink-900 text-cream-50 hover:bg-ink-800'
                        }`}
                      >
                        Get Started
                        <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-24 bg-ink-900">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-500/30 to-transparent" />

        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-gold-500" />
              <span className="text-gold-400 text-sm font-sans tracking-[0.2em] uppercase">
                Add-On Services
              </span>
              <span className="w-8 h-px bg-gold-500" />
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-cream-50 mb-4">Additional Services</h2>
            <p className="font-body text-cream-300 max-w-2xl mx-auto">
              Enhance your author platform with these powerful add-ons.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {additionalServices.map((service, index) => (
              <div key={index} className="group p-8 bg-ink-800/50 border border-ink-700 hover:border-gold-500/30 transition-all duration-500">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-gold-500/20 text-gold-400 rounded-lg group-hover:scale-110 transition-transform duration-300">
                    {service.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display text-xl font-semibold text-cream-50 mb-1">{service.title}</h3>
                    <div className="text-gold-400 font-sans font-medium mb-3">{service.price}</div>
                    <p className="font-body text-cream-400 mb-4">{service.description}</p>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm">
                          <Check className="w-4 h-4 text-gold-500" />
                          <span className="text-cream-300">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-cream-100">
        <div className="container mx-auto px-6 lg:px-12 text-center max-w-3xl">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-ink-900 mb-6">
            Ready to Build Your Author Website?
          </h2>
          <p className="font-body text-xl text-ink-600 mb-10">
            Take the first step toward building your online presence and connecting with more readers.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={scrollToContact}
              className="btn-primary"
            >
              <span className="flex items-center gap-3">
                Schedule a Consultation
                <ArrowRight className="w-5 h-5" />
              </span>
            </button>
            <a href="/demo-preview" className="btn-secondary">
              View Portfolio
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
